"use client";

import { useSession } from "@/components/SupabaseProvider";
import { useQuery } from "@tanstack/react-query";
import { useProfile } from "./useProfile";

export const useSubscriptions = () => {
  const session = useSession();
  const { data: profile, isLoading: profileIsLoading } = useProfile();
  // Only a Stripe customer can have subscriptions. The profile gets its
  // customer_id when the user first opens a checkout, so a user back from
  // Stripe already has it. Without this gate every free user's page view
  // asked (SubscriptionIssueBanner is mounted app-wide) and got a 400.
  const hasCustomer = !!profile?.customerId;
  const query = useQuery({
    enabled: !!session && hasCustomer,
    queryKey: ["subscriptions"],
    // Billing actions invalidate ["subscriptions"] themselves, so mounts,
    // window focus and the banner's second copy can reuse the cached list.
    staleTime: 5 * 60 * 1000,
    // A 4xx (expired session) fails the same way on every retry.
    retry: (failureCount, error) => {
      const status = (error as Error & { status?: number }).status;
      return failureCount < 3 && !(status && status < 500);
    },
    queryFn: async () => {
      if (!session) return;

      const r = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/billing/subscription`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );
      if (!r.ok)
        throw Object.assign(
          new Error(`GET /billing/subscription failed (${r.status})`),
          { status: r.status }
        );
      return r.json();
    },
  });

  return {
    ...query,
    // The query waits for the profile, so keep readers on their loading
    // state until it knows whether to run instead of flashing "no plan".
    isLoading: query.isLoading || (!!session && profileIsLoading),
  };
};
